import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, MapPinIcon, XMarkIcon, MapIcon, GlobeAltIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { placesAPI } from '../../services/api';
import MapPinDrop from './MapPinDrop';

interface LocationData {
  address: string;
  lat: number;
  lng: number;
}

interface LocationPickerSimpleProps {
  initialLocation?: LocationData;
  onLocationSelect: (location: LocationData) => void;
  label?: string;
  defaultCountry?: string;
}

interface Prediction {
  description: string;
  place_id: string;
}

interface Country {
  code: string;
  name: string;
  flag: string;
  currency: string;
}

const SUPPORTED_COUNTRIES: Country[] = [
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', currency: 'NGN' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭', currency: 'GHS' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪', currency: 'KES' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', currency: 'ZAR' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP' },
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', currency: 'CAD' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪', currency: 'AED' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR' },
  { code: 'FR', name: 'France', flag: '🇫🇷', currency: 'EUR' },
  { code: 'RW', name: 'Rwanda', flag: '🇷🇼', currency: 'RWF' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', currency: 'TZS' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬', currency: 'UGX' },
  { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', currency: 'ETB' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', currency: 'EGP' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦', currency: 'MAD' },
];

const LocationPickerSimple: React.FC<LocationPickerSimpleProps> = ({
  initialLocation,
  onLocationSelect,
  label = 'Select Location',
  defaultCountry
}) => {
  const [searchQuery, setSearchQuery] = useState(initialLocation?.address || '');
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    initialLocation || null
  );
  const [showMapPinDrop, setShowMapPinDrop] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    SUPPORTED_COUNTRIES.find(c => c.code === defaultCountry) || SUPPORTED_COUNTRIES[0]
  );
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [isDetectingCountry, setIsDetectingCountry] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!defaultCountry) {
      detectUserCountry();
    }
  }, [defaultCountry]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const detectUserCountry = async () => {
    setIsDetectingCountry(true);
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.country_code) {
        const detectedCountry = SUPPORTED_COUNTRIES.find(
          c => c.code === data.country_code.toUpperCase()
        );
        if (detectedCountry) {
          setSelectedCountry(detectedCountry);
        }
      }
    } catch (error) {
      console.log('Could not detect country, using default');
    } finally {
      setIsDetectingCountry(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 3) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await placesAPI.autocomplete(query, selectedCountry.code);

        if (data.predictions) {
          setPredictions(data.predictions);
          setShowPredictions(true);
        }
      } catch (error) {
        console.error('Address search error:', error);
      }
    }, 300);
  };

  const handlePredictionSelect = async (prediction: Prediction) => {
    try {
      const data = await placesAPI.details(prediction.place_id);

      const location: LocationData = {
        address: data.formatted_address,
        lat: data.lat,
        lng: data.lng
      };

      setSelectedLocation(location);
      setSearchQuery(location.address);
      setShowPredictions(false);
      onLocationSelect(location);
    } catch (error) {
      console.error('Location details error:', error);
    }
  };

  const handleMapLocationSelect = (location: LocationData) => {
    setSelectedLocation(location);
    setSearchQuery(location.address);
    onLocationSelect(location);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    if (searchQuery.length >= 3) {
      setPredictions([]);
      setShowPredictions(false);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const data = await placesAPI.autocomplete(searchQuery, country.code);
          if (data.predictions) {
            setPredictions(data.predictions);
            setShowPredictions(true);
          }
        } catch (error) {
          console.error('Address search error:', error);
        }
      }, 100);
    }
  };

  const clearSelection = () => {
    setSearchQuery('');
    setPredictions([]);
    setShowPredictions(false);
    setSelectedLocation(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-700">{label}</label>
        <button
          type="button"
          onClick={() => setShowMapPinDrop(true)}
          className="flex items-center space-x-1 text-sm text-sky-600 hover:text-sky-700 font-medium transition-colors"
        >
          <MapIcon className="h-4 w-4" />
          <span>Use Map</span>
        </button>
      </div>

      <div className="flex space-x-2">
        <div className="relative" ref={countryDropdownRef}>
          <button
            type="button"
            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
            className="flex items-center space-x-1 px-3 py-2.5 border border-gray-300 rounded-volexx bg-white hover:bg-gray-50 transition-colors min-w-[100px]"
            disabled={isDetectingCountry}
          >
            {isDetectingCountry ? (
              <span className="text-gray-400 text-sm">...</span>
            ) : (
              <>
                <span className="text-lg">{selectedCountry.flag}</span>
                <span className="text-sm font-medium text-gray-700">{selectedCountry.code}</span>
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </>
            )}
          </button>

          {showCountryDropdown && (
            <div className="absolute z-[60] mt-1 w-56 bg-white border border-gray-200 rounded-volexx shadow-volexx-lg max-h-60 overflow-y-auto">
              {SUPPORTED_COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors flex items-center space-x-2 ${
                    selectedCountry.code === country.code ? 'bg-sky-50' : ''
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="text-sm font-medium text-gray-700">{country.name}</span>
                  <span className="text-xs text-gray-400 ml-auto">{country.currency}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={`Search address in ${selectedCountry.name}...`}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
              required
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSelection}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {showPredictions && predictions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-volexx shadow-volexx-lg max-h-60 overflow-y-auto">
              {predictions.map((prediction, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handlePredictionSelect(prediction)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <div className="flex items-start space-x-2">
                    <MapPinIcon className="h-5 w-5 text-volexx-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{prediction.description}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!showPredictions && predictions.length === 0 && searchQuery.length >= 3 && !selectedLocation && (
            <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-700">
                Can't find your address?{' '}
                <button
                  type="button"
                  onClick={() => setShowMapPinDrop(true)}
                  className="font-semibold underline hover:text-orange-800"
                >
                  Pin it on the map
                </button>
                {' '}or try a different country.
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedLocation && (
        <div className="bg-volexx-blue-50 border border-volexx-blue-200 rounded-volexx p-3">
          <div className="flex items-start space-x-2">
            <MapPinIcon className="h-5 w-5 text-volexx-blue-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{selectedLocation.address}</p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedCountry.flag} {selectedCountry.name} • Coordinates: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
              </p>
            </div>
          </div>
        </div>
      )}

      <MapPinDrop
        isOpen={showMapPinDrop}
        onClose={() => setShowMapPinDrop(false)}
        onLocationSelect={handleMapLocationSelect}
        initialLocation={selectedLocation || undefined}
      />
    </div>
  );
};

export default LocationPickerSimple;
